import countries from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";
import { serviceLabel } from "./requestDisplay";

countries.registerLocale(fr);

// What the detail window shows for each type of request

export const ACTIVITY_SECTORS = {
  COM: "Commerce général",
  BTP: "BTP",
  INF: "Services informatiques",
  RES: "Restauration",
  IMM: "Activités immobilières (SCI)",
  VTC: "Transport VTC",
  ARG: "Agence de transfert d'argent",
  VOY: "Agence de voyage",
  ENT: "Services aux entreprises",
  FRE: "Consultation et freelance",
  CAB: "Cabinet de conseil",
  AUT: "Autre",
};

const PASSPORT_TYPES = {
  first_time: "Premier passeport",
  expired: "Passeport périmé",
  lost: "Passeport perdu",
  stolen: "Passeport volé",
  damaged: "Passeport détérioré",
  affiliation_change: "Changement de filiation",
  name_change: "Changement de nom",
  correction: "Correction",
};

export const DOCUMENT_LABELS = {
  id: "Pièce d'identité ou acte de naissance",
  witness1Id: "Pièce d'identité du premier témoin",
  witness2Id: "Pièce d'identité du second témoin",
  letter: "Demande manuscrite",
  uin: "Attestation NIU",
  location: "Plan de localisation",
  neighborBill: "Facture du voisin",
  equipment: "Liste des équipements",
  invoice: "Facture commerciale",
  ticket: "Connaissement ou titre de transport",
  originCert: "Certificat d'origine",
  natureCert: "Certificat de nature du bien",
  extra: "Document complémentaire",
  projectDescription: "Description du projet",
  companyRegCert: "Registre de commerce",
  noRoyaltyCert: "Attestation d'exonération de patente",
  taxPayerCard: "Attestation d'immatriculation",
  nc: "Formulaire NC 501",
  nc501: "Formulaire NC 501",
  existingLogo: "Logo existant",
  rccm: "Registre de commerce",
  managerId: "Pièce d'identité du représentant",
  idCard: "Pièce d'identité",
  logo: "Logo à protéger",
  taxRegistrationCert: "Attestation d'immatriculation",
  taxComplianceCert: "Attestation de conformité fiscale",
  locationPlan: "Plan de localisation",
  employeesList: "Liste des employés",
};

const country = (code) => (code ? countries.getName(code, "fr") || code : null);
const sector = (code) => ACTIVITY_SECTORS[code] || code;

const companyRows = (company) =>
  company
    ? [
        ["Entreprise", company.businessName],
        ["Forme juridique", company.businessType],
        ["Secteur d'activité", sector(company.activitySector)],
        ["Siège social", company.address],
        ["Pays", country(company.country)],
      ]
    : [];

const HOLDER_TYPES = {
  person: "Personne physique",
  company: "Entreprise",
};

const applicantRows = (r) => [
  ["Adresse", r.address],
  ["Téléphone", r.phone],
  ["Adresse e-mail", r.email],
];

const businessRows = (r) => [
  ["Nom de l'entreprise", r.businessName],
  ["Téléphone de l'entreprise", r.companyPhone],
  ["Adresse e-mail de l'entreprise", r.companyEmail],
];

const DETAILS = {
  TrademarkRequest: {
    rows: (r) => [
      ["Pays", country(r.country)],
      ["Titulaire", HOLDER_TYPES[r.holderType]],
      ["Nom de la marque", r.brandName],
      ["Nom de l'entreprise", r.businessName],
    ],
    documents: ["idCard", "logo"],
  },
  TaxRegistrationRequest: {
    rows: (r) => [
      ["Pays", country(r.country)],
      ["Déjà immatriculé", r.alreadyRegistered ? "Oui" : "Non"],
      ["NIU", r.niu],
      ["Demandeur", HOLDER_TYPES[r.holderType]],
      ...applicantRows(r),
      ["Profession", r.profession],
      ...businessRows(r),
    ],
    documents: ["idCard", "rccm"],
  },
  TaxComplianceRequest: {
    rows: (r) => [
      ["Pays", country(r.country)],
      ["NIU", r.niu],
    ],
    documents: [],
  },
  CnpsRegistrationRequest: {
    rows: (r) => [
      ["Pays", country(r.country)],
      ...applicantRows(r),
      ...businessRows(r),
    ],
    documents: [
      "rccm",
      "taxRegistrationCert",
      "taxComplianceCert",
      "locationPlan",
      "employeesList",
    ],
  },
  VisualIdentityRequest: {
    rows: (r) => [
      ["Nom à faire figurer", r.businessName],
      ["Supports commandés", (r.services || []).map(serviceLabel).join(", ")],
      ["Précisions", r.details],
    ],
    documents: ["existingLogo"],
  },
  CriminalRecordRequest: {
    rows: (r) => [
      [
        "Lieu de naissance",
        [r.city, country(r.country)].filter(Boolean).join(", "),
      ],
    ],
    documents: ["id"],
  },
  NationalityCertificateRequest: {
    rows: (r) => [["Pays", country(r.country)]],
    documents: ["id"],
  },
  CelibacyCertificateRequest: {
    rows: (r) => [["Pays", country(r.country)]],
    documents: ["id", "witness1Id", "witness2Id"],
  },
  EneoRequest: {
    rows: (r) => [
      ["Pays", country(r.country)],
      [
        "Type de compteur",
        { prepaid: "Prépayé", postpaid: "Postpayé" }[r.serviceType],
      ],
    ],
    documents: ["id", "letter", "uin", "location", "neighborBill", "equipment"],
  },
  CustomsRebateRequest: {
    rows: (r) => [
      ["Pays", country(r.country)],
      ["Famille de produits", r.productType],
    ],
    documents: ["invoice", "ticket", "originCert", "natureCert", "extra"],
  },
  BusinessPlanRequest: {
    rows: (r) => [
      ["Nom du projet", r.projectName],
      ["Secteur d'activité", sector(r.activitySector)],
      ["Pays", country(r.country)],
    ],
    documents: ["projectDescription", "extra"],
  },
  ImportationRequest: {
    rows: (r) => [
      [
        "Procédure",
        { standard: "Standard", express: "Express" }[r.serviceType],
      ],
      ...companyRows(r.company),
    ],
    documents: ["companyRegCert", "noRoyaltyCert", "taxPayerCard"],
  },
  OapiRequest: {
    rows: (r) => companyRows(r.company),
    documents: ["rccm", "managerId", "nc", "nc501", "extra"],
  },
  PassportEnrollmentRequest: {
    rows: (r) => [
      ["Type de demande", PASSPORT_TYPES[r.passportType] || r.passportType],
      ["Destination", r.destination],
      ["Motif", r.reason],
      ["Date de naissance", r.birthDate],
      [
        "Lieu de naissance",
        [r.birthPlace, r.birthDept, r.birthRegion, country(r.birthCountry)]
          .filter(Boolean)
          .join(", "),
      ],
      [
        "Résidence",
        [
          r.residencyStreet,
          r.residencyPlace,
          r.residencyDept,
          r.residencyRegion,
          country(r.residencyCountry),
        ]
          .filter(Boolean)
          .join(", "),
      ],
      ["Nom de la mère", r.motherName],
      ["Nom du père", r.fatherName],
      ["Numéro de pièce d'identité", r.nicNumber || r.previousPassportNumber],
      ["Date de délivrance", r.nicIssueDate || r.previousPassportIssueDate],
    ],
    documents: ["id"],
  },
};

// Rows with a value, and the files attached to the request
export const detailsFor = (request) => {
  const config = DETAILS[request?.type];
  if (!config) return { rows: [], documents: [] };

  const rows = config.rows(request).filter(([, value]) => value);
  const documents = config.documents
    .filter((key) => request[key]?.url)
    .map((key) => ({
      key,
      label: DOCUMENT_LABELS[key] || key,
      file: request[key],
    }));

  return { rows, documents };
};
